import type { ClassNow, GoalsView, ParentGoals, ParentPrefs, Subject } from "@tutor/protocol";
import { Icon } from "@tutor/ui";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import * as api from "./api";
import { ClassNowGroup } from "./ClassNowGroup";
import { Chips, Row, Stepper, TimeSelect, Toggle } from "./controls";
import { errorKey } from "./errors";
import { useParentT, type ParentStringKey } from "./strings";

// Goals editor. The engine enforces these limits (rule 1); the panel only edits them.
// Every change saves itself after a short pause, sending the version last read; a 409
// means another device saved first, so the latest version is shown instead.

/** Pause after the last change before it is saved. */
export const AUTOSAVE_MS = 500;

interface Notice {
  readonly key: ParentStringKey;
  readonly tone: "info" | "error";
}

type Status = "idle" | "saving" | "saved";

function inRange(goals: ParentGoals): boolean {
  return (
    Number.isInteger(goals.dailyMinutesCap) &&
    goals.dailyMinutesCap >= 0 &&
    goals.dailyMinutesCap <= 600 &&
    Number.isInteger(goals.maxDifficulty) &&
    goals.maxDifficulty >= 1 &&
    goals.maxDifficulty <= 10
  );
}

export function Goals(props: {
  profileId: string;
  /** Bumped when the live channel says these goals changed. */
  refresh: number;
  /** Handles a stale session; returns true when it did. */
  onAuthError: (error: api.ApiError) => boolean;
}) {
  const { profileId, refresh, onAuthError } = props;
  const t = useParentT();
  const ids = useId();
  const [goals, setGoals] = useState<ParentGoals | null>(null);
  const [prefs, setPrefs] = useState<ParentPrefs | null>(null);
  const [classNow, setClassNow] = useState<ClassNow | null>(null);
  const [notice, setNotice] = useState<Notice>();
  const [status, setStatus] = useState<Status>("idle");

  // What the next save sends, kept in refs so a save in flight always reads the latest.
  const latest = useRef<{
    goals: ParentGoals;
    prefs: ParentPrefs;
    classNow: ClassNow | null;
  } | null>(null);
  const version = useRef<number | null>(null);
  const edits = useRef(0);
  const saving = useRef(false);
  const again = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const show = useCallback((next: GoalsView) => {
    version.current = next.version;
    latest.current = { goals: next.goals, prefs: next.prefs, classNow: next.classNow ?? null };
    setGoals(next.goals);
    setPrefs(next.prefs);
    setClassNow(next.classNow ?? null);
  }, []);

  const load = useCallback(
    async (after?: Notice) => {
      const res = await api.getGoals(profileId);
      if (!res.ok) {
        if (!onAuthError(res.error)) setNotice({ key: errorKey(res.error), tone: "error" });
        return;
      }
      show(res.value);
      setNotice(after);
    },
    [profileId, onAuthError, show],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Someone saved these goals: reload, and say so unless it was our own save coming back.
  const [seenRefresh, setSeenRefresh] = useState(refresh);
  useEffect(() => {
    if (refresh === seenRefresh) return;
    setSeenRefresh(refresh);
    void api.getGoals(profileId).then((res) => {
      if (!res.ok || res.value.version === version.current) return;
      show(res.value);
      setNotice({ key: "goals.changedElsewhere", tone: "info" });
    });
  }, [refresh, seenRefresh, profileId, show]);

  const save = useCallback(async (): Promise<void> => {
    const draft = latest.current;
    if (draft === null || version.current === null) return;
    if (saving.current) {
      again.current = true;
      return;
    }
    if (!inRange(draft.goals)) {
      setNotice({ key: "goals.invalid", tone: "error" });
      return;
    }
    saving.current = true;
    setStatus("saving");
    const sent = edits.current;
    const res = await api.putGoals(profileId, { ...draft, version: version.current });
    saving.current = false;
    if (res.ok) {
      version.current = res.value.version;
      setNotice(undefined);
      // Changed again while this save was in flight: keep the newer edits and send them.
      if (again.current || edits.current !== sent) {
        again.current = false;
        await save();
        return;
      }
      show(res.value);
      setStatus("saved");
      return;
    }
    again.current = false;
    setStatus("idle");
    if (onAuthError(res.error)) return;
    if (res.error.kind === "http" && res.error.status === 409) {
      const current = api.conflictCurrent(res.body);
      if (current === undefined) await load({ key: "goals.conflict", tone: "error" });
      else {
        show(current);
        setNotice({ key: "goals.conflict", tone: "error" });
      }
      return;
    }
    setNotice({
      key:
        res.error.kind === "http" && res.error.status === 400
          ? "goals.invalid"
          : errorKey(res.error),
      tone: "error",
    });
  }, [profileId, onAuthError, load, show]);

  // Leaving the tab (or parent mode) with a change still waiting saves it right away.
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(
    () => () => {
      if (timer.current === undefined) return;
      clearTimeout(timer.current);
      void saveRef.current();
    },
    [],
  );

  if (goals === null || prefs === null) {
    return notice === undefined ? (
      <p className="parent-muted">{t("parent.loading")}</p>
    ) : (
      <p className="parent-message" role="alert">
        {t(notice.key)}
      </p>
    );
  }

  const edit = (next: { goals?: ParentGoals; prefs?: ParentPrefs; classNow?: ClassNow | null }) => {
    const draft = {
      goals: next.goals ?? goals,
      prefs: next.prefs ?? prefs,
      classNow: next.classNow === undefined ? classNow : next.classNow,
    };
    latest.current = draft;
    edits.current += 1;
    setGoals(draft.goals);
    setPrefs(draft.prefs);
    setClassNow(draft.classNow);
    setStatus("idle");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = undefined;
      void save();
    }, AUTOSAVE_MS);
  };

  const id = (name: string) => `${ids}-${name}`;
  const info = (label: string) => t("goals.info", { label });
  const toggleRow = (
    name: string,
    label: string,
    checked: boolean,
    change: (on: boolean) => void,
    hint?: string,
  ) => (
    <Row
      label={label}
      labelId={id(name)}
      hintLabel={info(label)}
      {...(hint === undefined ? {} : { hint })}
    >
      <Toggle labelId={id(name)} checked={checked} onChange={change} />
    </Row>
  );

  return (
    <div className="pm-goals">
      <p className="pm-save" role="status" data-status={status}>
        {status === "saved" ? <Icon name="check" size={16} /> : null}
        {t(
          status === "saving"
            ? "goals.saving"
            : status === "saved"
              ? "goals.saved"
              : "goals.autosave",
        )}
      </p>
      {notice === undefined ? null : (
        <div
          className={
            notice.tone === "info" ? "parent-message parent-message-info" : "parent-message"
          }
          role={notice.tone === "info" ? "status" : "alert"}
        >
          <span>{t(notice.key)}</span>
          {notice.tone === "error" && notice.key !== "goals.conflict" ? (
            <button type="button" className="parent-link" onClick={() => void save()}>
              {t("parent.retry")}
            </button>
          ) : null}
        </div>
      )}

      <section className="pm-group" aria-labelledby={id("practice")}>
        <h3 id={id("practice")} className="pm-group__title">
          {t("goals.group.practice")}
        </h3>
        <div className="pm-row pm-row--block">
          <span id={id("subjects")} className="pm-row__label">
            {t("goals.subjects")}
          </span>
          <Chips
            labelId={id("subjects")}
            options={api.SUBJECTS.map((s) => ({ value: s, label: t(`subject.${s}`) }))}
            selected={goals.allowedSubjects}
            onToggle={(subject: Subject, on) => {
              const set = new Set(goals.allowedSubjects);
              if (on) set.add(subject);
              else set.delete(subject);
              edit({
                goals: { ...goals, allowedSubjects: api.SUBJECTS.filter((s) => set.has(s)) },
              });
            }}
          />
          {goals.allowedSubjects.length === 0 ? (
            <p className="pm-row__hint pm-row__hint--warn">{t("goals.noSubject")}</p>
          ) : null}
        </div>
        <Row label={t("goals.dailyMinutes")} labelId={id("minutes")}>
          <Stepper
            labelId={id("minutes")}
            value={goals.dailyMinutesCap}
            min={0}
            max={600}
            step={5}
            format={(n) => t("goals.minutes", { count: n })}
            lessLabel={t("goals.less")}
            moreLabel={t("goals.more")}
            onChange={(n) => {
              edit({ goals: { ...goals, dailyMinutesCap: n } });
            }}
          />
        </Row>
        <Row
          label={t("goals.maxDifficulty")}
          labelId={id("level")}
          hint={t("goals.maxDifficultyHint")}
          hintLabel={info(t("goals.maxDifficulty"))}
        >
          <Stepper
            labelId={id("level")}
            value={goals.maxDifficulty}
            min={1}
            max={10}
            step={1}
            format={(n) => t("goals.level", { level: n })}
            lessLabel={t("goals.less")}
            moreLabel={t("goals.more")}
            onChange={(n) => {
              edit({ goals: { ...goals, maxDifficulty: n } });
            }}
          />
        </Row>
        <Row
          label={t("goals.quietHours")}
          labelId={id("quiet")}
          hint={t("goals.quietHint")}
          hintLabel={info(t("goals.quietHours"))}
        >
          <span className="pm-range" role="group" aria-labelledby={id("quiet")}>
            <TimeSelect
              label={t("goals.quietStart")}
              value={goals.quietStart}
              onChange={(m) => {
                edit({ goals: { ...goals, quietStart: m } });
              }}
            />
            <span aria-hidden="true">–</span>
            <TimeSelect
              label={t("goals.quietEnd")}
              value={goals.quietEnd}
              onChange={(m) => {
                edit({ goals: { ...goals, quietEnd: m } });
              }}
            />
          </span>
        </Row>
      </section>

      <ClassNowGroup
        value={classNow}
        onChange={(next) => {
          edit({ classNow: next });
        }}
        t={t}
        id={id}
      />

      <section className="pm-group" aria-labelledby={id("coach")}>
        <h3 id={id("coach")} className="pm-group__title">
          {t("goals.group.coach")}
        </h3>
        {toggleRow(
          "ask",
          t("goals.askParent"),
          goals.askParentOnFrustration,
          (on) => {
            edit({ goals: { ...goals, askParentOnFrustration: on } });
          },
          t("goals.askParentHint"),
        )}
        {toggleRow(
          "free",
          t("goals.freeQuestions"),
          // A view saved before ADR-0042 has no switch: free questions are on.
          prefs.freeQuestions !== false,
          (on) => {
            edit({ prefs: { ...prefs, freeQuestions: on } });
          },
          t("goals.freeQuestionsHint"),
        )}
        {/* Sent only where the trial is open (ADR-0063). */}
        {prefs.oneBoard != null &&
          toggleRow(
            "board",
            t("goals.oneBoard"),
            prefs.oneBoard,
            (on) => {
              edit({ prefs: { ...prefs, oneBoard: on } });
            },
            t("goals.oneBoardHint"),
          )}
      </section>

      <section className="pm-group" aria-labelledby={id("notify")}>
        <h3 id={id("notify")} className="pm-group__title">
          {t("goals.group.notify")}
        </h3>
        {toggleRow("now", t("goals.notifyNow"), prefs.notifyFrustrationNow, (on) => {
          edit({ prefs: { ...prefs, notifyFrustrationNow: on } });
        })}
        {toggleRow("digest", t("goals.digest"), prefs.progressDigest, (on) => {
          edit({ prefs: { ...prefs, progressDigest: on } });
        })}
        {toggleRow("weekly", t("goals.weeklyEmail"), prefs.weeklyEmail, (on) => {
          edit({ prefs: { ...prefs, weeklyEmail: on } });
        })}
      </section>
    </div>
  );
}
